import React, { useState, useEffect, useContext } from "react";
import { DataContext } from "../contexts/DataContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFilter,
  faSpinner,
  faFileExcel,
  faCalendarAlt,
  faTimes,
  faSync,
  faChartBar,
  faTable,
  faSearch,
  faInfoCircle,
  faShip,
  faTruck,
  faPlane,
  faExclamationTriangle,
  faClock,
  faFileInvoice,
  faClipboardCheck,
  faExpand,
  faCompress,
  faColumns,
  faChevronDown,
  faChevronUp,
  faMapMarkerAlt,
  faIdCard,
  faBuilding,
  faDownload,
  faCheckCircle,
  faTimesCircle,
  faBell,
  faHourglassHalf,
} from "@fortawesome/free-solid-svg-icons";
import Select from "react-select";
import { DateRangePicker } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";

// Import subcomponents
import DashboardSummaryCards from "./DashboardSummaryCards";
import StatusDistributionChart from "./StatusDistributionChart";
import ValidationProcessTable from "./ValidationProcessTable";
import EtaComplianceChart from "./EtaComplianceChart";
import ProcessingTimeChart from "./ProcessingTimeChart";
import ReferencesDataGrid from "./ReferencesDataGrid";
import DetailedReferencePanel from "./DetailedReferencePanel";
import ReferencesDetailModal from "./ReferencesDetailModal";

const TrafficManagementDashboard = () => {
  const { trafficData, isLoading, fetchTrafficData, setShouldFetchData } =
    useContext(DataContext);

  // State for layout and UI control
  const [activeTab, setActiveTab] = useState("overview");
  const [showDetailsPanel, setShowDetailsPanel] = useState(false);
  const [detailsItem, setDetailsItem] = useState(null);

  // State for the modal
  const [modalConfig, setModalConfig] = useState({
    show: false,
    title: "",
    description: "",
    references: [],
    kpiType: "",
  });

  // State for filters
  const [showFilters, setShowFilters] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [dateRange, setDateRange] = useState([
    {
      startDate: null,
      endDate: null,
      key: "selection",
    },
  ]);

  // State for managing data
  const [filteredData, setFilteredData] = useState([]);
  const [summaryMetrics, setSummaryMetrics] = useState({
    totalReferences: 0,
    etaComplianceCount: 0,
    validationProcessCount: 0,
    averageProcessingTime: 0,
    outOfTimeReferences: 0,
  });

  // Filter states
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState([]);
  const [selectedEntryPoints, setSelectedEntryPoints] = useState([]);
  const [selectedOperationTypes, setSelectedOperationTypes] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  // Filter options from data
  const [filterOptions, setFilterOptions] = useState({
    customers: [],
    status: [],
    entryPoints: [],
    operationTypes: [],
  });

  // Chart and layout settings
  const [chartLayout, setChartLayout] = useState({
    columns: 2, // Default to 2 columns
    size: "medium", // Options: small, medium, large
  });

  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalPages: 1,
    totalItems: 0,
  });

  // Sort configuration
  const [sortConfig, setSortConfig] = useState({
    key: "header.detail.dates_data.date_init",
    direction: "desc",
  });

  // Functions to open modal with specific KPI data
  const showOutOfTimeModal = () => {
    const outOfTimeReferences = filteredData.filter((item) =>
      isOutOfTimeValidation(item)
    );
    setModalConfig({
      show: true,
      title: "Out of Time References",
      description: "References that have exceeded the 3-day validation window.",
      references: outOfTimeReferences,
      kpiType: "outOfTime",
    });
  };

  const showValidationProcessModal = () => {
    const inProcessReferences = filteredData.filter((item) =>
      isInValidationProcess(item)
    );
    setModalConfig({
      show: true,
      title: "References in Validation Process",
      description: "References currently within the 3-day validation window.",
      references: inProcessReferences,
      kpiType: "validationProcess",
    });
  };

  const showEtaComplianceModal = (compliant = false) => {
    const etaReferences = filteredData.filter((item) => {
      const result = calculateEtaCompliance(item);
      return result.daysDifference !== null && result.compliant === compliant;
    });

    setModalConfig({
      show: true,
      title: compliant
        ? "ETA Compliant References"
        : "ETA Non-Compliant References",
      description: compliant
        ? "References with ETA within 7 days from Alta date."
        : "References with ETA more than 7 days from Alta date.",
      references: etaReferences,
      kpiType: "etaCompliance",
    });
  };

  const showProcessingTimeModal = (type = "exit") => {
    const references = filteredData.filter((item) => {
      return type === "exit"
        ? calculateExitTime(item) !== null
        : calculatePaymentTime(item) !== null;
    });

    setModalConfig({
      show: true,
      title: type === "exit" ? "Exit Time Analysis" : "Payment Time Analysis",
      description:
        type === "exit"
          ? "Analysis of time between ETA and Crossing date."
          : "Analysis of time between ETA and Pedimento Payment date.",
      references: references,
      kpiType: "processingTime",
    });
  };

  const closeModal = () => {
    setModalConfig((prev) => ({
      ...prev,
      show: false,
    }));
  };

  // Helper functions for date operations
  const parseDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? null : date;
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    try {
      const dateObj = typeof date === "string" ? new Date(date) : date;
      if (isNaN(dateObj.getTime())) return "N/A";

      const options = { year: "numeric", month: "short", day: "numeric" };
      return dateObj.toLocaleDateString(undefined, options);
    } catch (error) {
      console.error("Error formatting date:", error);
      return "N/A";
    }
  };

  const differenceInDays = (date1, date2) => {
    if (!date1 || !date2) return null;

    const d1 = typeof date1 === "string" ? new Date(date1) : date1;
    const d2 = typeof date2 === "string" ? new Date(date2) : date2;

    if (isNaN(d1.getTime()) || isNaN(d2.getTime())) return null;

    const diffTime = Math.abs(d2 - d1);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Dashboard calculation functions
  const calculateEtaCompliance = (item) => {
    if (
      !item?.header?.ebookingDates?.fechaETA ||
      !item?.header?.ebookingDates?.fechaAlta
    ) {
      return { compliant: false, daysDifference: null };
    }

    const etaDate = parseDate(item.header.ebookingDates.fechaETA);
    const altaDate = parseDate(item.header.ebookingDates.fechaAlta);

    if (!etaDate || !altaDate) {
      return { compliant: false, daysDifference: null };
    }

    const daysDifference = differenceInDays(altaDate, etaDate);
    return { compliant: daysDifference <= 7, daysDifference };
  };

  const isInValidationProcess = (item) => {
    if (!item?.header?.ebookingDates?.fechaAlta) return false;

    const altaDate = parseDate(item.header.ebookingDates.fechaAlta);
    if (!altaDate) return false;

    const now = new Date();
    const daysSinceAlta = differenceInDays(altaDate, now);

    // Check if it's within 3 days from fechaAlta
    if (daysSinceAlta <= 3 && daysSinceAlta >= 0) {
      // Check if there's no "Alert" status in the timeline
      return !item.header.trafficStatusTimeline?.some(
        (status) => status.status === "Alert"
      );
    }

    return false;
  };

  const isOutOfTimeValidation = (item) => {
    if (!item?.header?.ebookingDates?.fechaAlta) return false;

    const altaDate = parseDate(item.header.ebookingDates.fechaAlta);
    if (!altaDate) return false;

    const now = new Date();
    const daysSinceAlta = differenceInDays(altaDate, now);

    // If it's more than 3 days from fechaAlta and doesn't have "Alert" status
    return (
      daysSinceAlta > 3 &&
      !item.header.trafficStatusTimeline?.some(
        (status) => status.status === "Alert"
      )
    );
  };

  const calculateExitTime = (item) => {
    if (
      !item?.header?.ebookingDates?.fechaETA ||
      !item?.header?.detail?.dates_data?.date_crossing
    ) {
      return null;
    }

    const etaDate = parseDate(item.header.ebookingDates.fechaETA);
    const crossingDate = parseDate(item.header.detail.dates_data.date_crossing);

    if (!etaDate || !crossingDate) {
      return null;
    }

    return differenceInDays(etaDate, crossingDate);
  };

  const calculatePaymentTime = (item) => {
    if (
      !item?.header?.ebookingDates?.fechaETA ||
      !item?.header?.detail?.dates_data?.date_pedimento_paid
    ) {
      return null;
    }

    const etaDate = parseDate(item.header.ebookingDates.fechaETA);
    const paymentDate = parseDate(
      item.header.detail.dates_data.date_pedimento_paid
    );

    if (!etaDate || !paymentDate) {
      return null;
    }

    return differenceInDays(etaDate, paymentDate);
  };

  // Initialize data fetching
  useEffect(() => {
    setShouldFetchData((prev) => ({
      ...prev,
      fetchTrafficData: true,
    }));
    fetchTrafficData();

    return () => {
      setShouldFetchData((prev) => ({
        ...prev,
        fetchTrafficData: false,
      }));
    };
  }, [fetchTrafficData, setShouldFetchData]);

  // Process traffic data to extract filter options
  useEffect(() => {
    if (trafficData && trafficData.length > 0) {
      // Extract filter options
      const customerOptions = Array.from(
        new Set(trafficData.map((item) => item.header.customer_name))
      )
        .filter(Boolean)
        .map((customer) => ({ value: customer, label: customer }));

      const statusOptions = Array.from(
        new Set(trafficData.map((item) => item.header.trafficStatus))
      )
        .filter(Boolean)
        .map((status) => ({ value: status, label: status }));

      const entryPointOptions = Array.from(
        new Set(
          trafficData
            .map((item) => item.header.pedimento?.pedimento_entry_point)
            .filter(Boolean)
        )
      ).map((entryPoint) => ({ value: entryPoint, label: entryPoint }));

      const operationTypeOptions = Array.from(
        new Set(trafficData.map((item) => item.header.operation_type))
      )
        .filter(Boolean)
        .map((operationType) => ({
          value: operationType,
          label: getOperationTypeText(operationType),
        }));

      setFilterOptions({
        customers: customerOptions,
        status: statusOptions,
        entryPoints: entryPointOptions,
        operationTypes: operationTypeOptions,
      });

      // Set filtered data to all data initially
      setFilteredData(trafficData);
    }
  }, [trafficData]);

  // Apply filters to data
  useEffect(() => {
    if (!trafficData) return;

    let filtered = trafficData;

    // Apply search term filter
    if (searchTerm) {
      const searchTermLower = searchTerm.toLowerCase();
      filtered = filtered.filter((item) => {
        // Search in reference, customer name, status, etc.
        return (
          item.header.reference?.toLowerCase().includes(searchTermLower) ||
          item.header.customer_name?.toLowerCase().includes(searchTermLower) ||
          item.header.trafficStatus?.toLowerCase().includes(searchTermLower) ||
          item.header.pedimento?.pedimento_number
            ?.toLowerCase()
            .includes(searchTermLower)
        );
      });
    }

    // Apply dropdown filters
    if (selectedCustomers.length) {
      filtered = filtered.filter((item) =>
        selectedCustomers.some(
          (selected) => selected.value === item.header.customer_name
        )
      );
    }

    if (selectedStatus.length) {
      filtered = filtered.filter((item) =>
        selectedStatus.some(
          (selected) => selected.value === item.header.trafficStatus
        )
      );
    }

    if (selectedEntryPoints.length) {
      filtered = filtered.filter((item) =>
        selectedEntryPoints.some(
          (selected) =>
            selected.value === item.header.pedimento?.pedimento_entry_point
        )
      );
    }

    if (selectedOperationTypes.length) {
      filtered = filtered.filter((item) =>
        selectedOperationTypes.some(
          (selected) => selected.value === item.header.operation_type
        )
      );
    }

    // Apply date range filter
    const { startDate, endDate } = dateRange[0];
    if (startDate && endDate) {
      filtered = filtered.filter((item) => {
        const dateInit = item.header.detail?.dates_data?.date_init
          ? new Date(item.header.detail.dates_data.date_init)
          : null;
        return dateInit && dateInit >= startDate && dateInit <= endDate;
      });
    }

    setFilteredData(filtered);

    // Calculate summary metrics
    const etaComplianceCount = filtered.filter(
      (item) => calculateEtaCompliance(item).compliant
    ).length;

    const validationProcessCount = filtered.filter((item) =>
      isInValidationProcess(item)
    ).length;

    const outOfTimeReferences = filtered.filter((item) =>
      isOutOfTimeValidation(item)
    ).length;

    // Calculate average processing times
    const exitTimes = filtered
      .map((item) => calculateExitTime(item))
      .filter((time) => time !== null);

    const paymentTimes = filtered
      .map((item) => calculatePaymentTime(item))
      .filter((time) => time !== null);

    const allTimes = [...exitTimes, ...paymentTimes];
    const averageProcessingTime = allTimes.length
      ? Math.round(allTimes.reduce((a, b) => a + b, 0) / allTimes.length)
      : 0;

    setSummaryMetrics({
      totalReferences: filtered.length,
      etaComplianceCount,
      validationProcessCount,
      averageProcessingTime,
      outOfTimeReferences,
    });

    // Update pagination
    const totalPages = Math.ceil(filtered.length / pagination.pageSize);
    setPagination((prev) => ({
      ...prev,
      totalPages: totalPages,
      totalItems: filtered.length,
      currentPage: 1, // Reset to first page when filters change
    }));
  }, [
    trafficData,
    searchTerm,
    selectedCustomers,
    selectedStatus,
    selectedEntryPoints,
    selectedOperationTypes,
    dateRange,
    pagination.pageSize,
  ]);

  // Helper functions
  const getOperationTypeText = (operationType) => {
    if (operationType === "1") return "Import";
    if (operationType === "2") return "Export";
    return operationType || "Unknown";
  };

  const getTransportIcon = (entryPoint) => {
    if (!entryPoint)
      return {
        icon: faShip,
        color: "text-gray-500",
        title: "Unknown Transport Type",
      };

    const transportIconsMap = {
      // Seafreight
      160: { icon: faShip, color: "text-blue-600", title: "Sea Freight" },
      510: { icon: faShip, color: "text-blue-600", title: "Sea Freight" },
      430: { icon: faShip, color: "text-blue-600", title: "Sea Freight" },

      // Roadfreight
      240: { icon: faTruck, color: "text-green-600", title: "Road Freight" },

      // Airfreight
      520: { icon: faPlane, color: "text-purple-600", title: "Air Freight" },
      521: { icon: faPlane, color: "text-purple-600", title: "Air Freight" },
      640: { icon: faPlane, color: "text-purple-600", title: "Air Freight" },

      // Default for unknown entry points
      default: {
        icon: faShip,
        color: "text-gray-500",
        title: "Unknown Transport Type",
      },
    };

    // Convert to number if it's a string
    const entryPointNum =
      typeof entryPoint === "string" ? parseInt(entryPoint, 10) : entryPoint;
    return transportIconsMap[entryPointNum] || transportIconsMap["default"];
  };

  const clearAllFilters = () => {
    setSelectedCustomers([]);
    setSelectedStatus([]);
    setSelectedEntryPoints([]);
    setSelectedOperationTypes([]);
    setSearchTerm("");
    setDateRange([
      {
        startDate: null,
        endDate: null,
        key: "selection",
      },
    ]);
  };

  const getStatusColorClass = (status) => {
    if (!status) return "bg-gray-100 text-gray-800";

    try {
      switch (status.toLowerCase()) {
        case "alert":
          return "bg-yellow-100 text-yellow-800";
        case "in progress":
          return "bg-blue-100 text-blue-800";
        case "arribo":
          return "bg-indigo-100 text-indigo-800";
        case "revalidacion":
          return "bg-purple-100 text-purple-800";
        case "pagado":
          return "bg-green-100 text-green-800";
        case "delivered":
          return "bg-emerald-100 text-emerald-800";
        default:
          return "bg-gray-100 text-gray-800";
      }
    } catch (error) {
      console.warn("Invalid status value:", status);
      return "bg-gray-100 text-gray-800";
    }
  };

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key) {
      direction = sortConfig.direction === "asc" ? "desc" : "asc";
    }
    setSortConfig({ key, direction });
  };

  const hasActiveFilters =
    selectedCustomers.length > 0 ||
    selectedStatus.length > 0 ||
    selectedEntryPoints.length > 0 ||
    selectedOperationTypes.length > 0 ||
    (dateRange[0].startDate && dateRange[0].endDate) ||
    searchTerm.length > 0;

  const handleRefreshData = () => {
    setShouldFetchData((prev) => ({
      ...prev,
      fetchTrafficData: true,
    }));
    fetchTrafficData();
  };

  const toggleChartLayout = () => {
    setChartLayout((prev) => ({
      ...prev,
      columns: prev.columns === 2 ? 3 : 2, // Toggle between 2 and 3 columns
    }));
  };

  const toggleChartSize = () => {
    const sizes = ["small", "medium", "large"];
    const currentIndex = sizes.indexOf(chartLayout.size);
    const nextIndex = (currentIndex + 1) % sizes.length;

    setChartLayout((prev) => ({
      ...prev,
      size: sizes[nextIndex],
    }));
  };

  const handlePageChange = (page) => {
    setPagination((prev) => ({
      ...prev,
      currentPage: page,
    }));
  };

  const handlePageSizeChange = (size) => {
    setPagination((prev) => ({
      ...prev,
      pageSize: size,
      currentPage: 1, // Reset to first page when changing page size
    }));
  };

  const handleDetailsClick = (item) => {
    setDetailsItem(item);
    setShowDetailsPanel(true);
  };

  // Calculate paginated data for the grid
  const paginatedData = (() => {
    // Sort data first
    const sortedData = [...filteredData].sort((a, b) => {
      const getNestedValue = (obj, path) => {
        if (!obj || !path) return null;
        const keys = path.split(".");
        return keys.reduce(
          (o, key) => (o && o[key] !== undefined ? o[key] : null),
          obj
        );
      };

      let aVal = getNestedValue(a, sortConfig.key);
      let bVal = getNestedValue(b, sortConfig.key);

      // Handle date comparison
      if (sortConfig.key.includes("date") && aVal && bVal) {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      }

      if (aVal === null && bVal === null) return 0;
      if (aVal === null) return 1;
      if (bVal === null) return -1;

      if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });

    // Apply pagination
    const startIndex = (pagination.currentPage - 1) * pagination.pageSize;
    const endIndex = startIndex + pagination.pageSize;
    return sortedData.slice(startIndex, endIndex);
  })();

  const customSelectStyles = {
    control: (base, state) => ({
      ...base,
      borderColor: state.isFocused ? "#3b82f6" : "#e2e8f0",
      boxShadow: state.isFocused ? "0 0 0 1px #3b82f6" : "none",
      "&:hover": {
        borderColor: "#cbd5e1",
      },
      borderRadius: "0.375rem",
      minHeight: "38px",
      transition: "all 0.2s ease",
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: "#e1effe",
      borderRadius: "0.25rem",
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: "#1e40af",
      fontWeight: 500,
      fontSize: "0.8125rem",
      padding: "0.125rem 0.25rem",
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: "#60a5fa",
      ":hover": {
        backgroundColor: "#bfdbfe",
        color: "#1e3a8a",
      },
      borderRadius: "0 0.25rem 0.25rem 0",
    }),
    placeholder: (base) => ({
      ...base,
      color: "#94a3b8",
      fontSize: "0.875rem",
    }),
    menu: (base) => ({
      ...base,
      zIndex: 50, // Increased z-index to make sure it appears above other elements
      borderRadius: "0.375rem",
      boxShadow:
        "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
      border: "1px solid #e2e8f0",
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "#3b82f6"
        : state.isFocused
        ? "#e1effe"
        : "white",
      color: state.isSelected ? "white" : "#1e293b",
      fontSize: "0.875rem",
      padding: "0.5rem 0.75rem",
      ":active": {
        backgroundColor: state.isSelected ? "#3b82f6" : "#bfdbfe",
      },
    }),
  };

  // Determine chart size class based on layout settings
  const getChartSizeClass = () => {
    const columnClasses = {
      1: "grid-cols-1",
      2: "grid-cols-1 lg:grid-cols-2",
      3: "grid-cols-1 md:grid-cols-2 xl:grid-cols-3",
    };

    const sizeClasses = {
      small: "h-64",
      medium: "h-80",
      large: "h-96",
    };

    return {
      columns: columnClasses[chartLayout.columns],
      height: sizeClasses[chartLayout.size],
    };
  };

  // Render loading spinner when data is loading
  if (isLoading && (!trafficData || trafficData.length === 0)) {
    return (
      <div className="flex justify-center items-center h-screen">
        <FontAwesomeIcon
          icon={faSpinner}
          className="text-3xl font-semibold animate-spin text-blue-600 mr-3"
        />
        <div className="text-2xl font-semibold">Loading Dashboard...</div>
      </div>
    );
  }

  // Get chart size styling
  const chartSizeClasses = getChartSizeClass();

  return (
    <div className="bg-gray-50 min-h-screen flex flex-col">
      {/* Header with title and tabs */}
      <div className="w-full bg-white shadow-lg overflow-hidden">
        <div className="bg-gradient-to-r from-[#003366] to-blue-700 text-white">
          {/* Main header with title and action buttons */}
          <div className="flex justify-between items-center px-6 pt-5 pb-6">
            <div className="flex items-center space-x-3">
              <FontAwesomeIcon icon={faShip} className="text-2xl" />
              <h1 className="text-2xl font-bold tracking-tight">
                Traffic Management Dashboard
              </h1>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={handleRefreshData}
                className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200"
                disabled={isLoading}
              >
                <FontAwesomeIcon
                  icon={isLoading ? faSpinner : faSync}
                  className={isLoading ? "animate-spin" : ""}
                />
                <span>{isLoading ? "Refreshing..." : "Refresh Data"}</span>
              </button>
              <button
                onClick={toggleChartLayout}
                className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200"
              >
                <FontAwesomeIcon icon={faColumns} />
                <span>{`${chartLayout.columns} Columns`}</span>
              </button>
              <button
                onClick={toggleChartSize}
                className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200"
              >
                <FontAwesomeIcon
                  icon={chartLayout.size === "large" ? faCompress : faExpand}
                />
                <span>{`${
                  chartLayout.size.charAt(0).toUpperCase() +
                  chartLayout.size.slice(1)
                } Size`}</span>
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex px-6 border-b border-blue-600">
            <button
              className={`px-4 py-2 font-medium text-sm rounded-t-lg transition-colors duration-200 ${
                activeTab === "overview"
                  ? "bg-white text-blue-800"
                  : "text-blue-100 hover:bg-blue-800"
              }`}
              onClick={() => setActiveTab("overview")}
            >
              <FontAwesomeIcon icon={faChartBar} className="mr-2" />
              Overview
            </button>
            <button
              className={`px-4 py-2 font-medium text-sm rounded-t-lg transition-colors duration-200 ${
                activeTab === "data"
                  ? "bg-white text-blue-800"
                  : "text-blue-100 hover:bg-blue-800"
              }`}
              onClick={() => setActiveTab("data")}
            >
              <FontAwesomeIcon icon={faTable} className="mr-2" />
              Data Grid
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 p-6">
        {/* Search and Filter Bar */}
        <div className="mb-6">
          <div className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden">
            <div className="p-4">
              <div className="flex flex-col md:flex-row gap-3">
                {/* Search input */}
                <div className="w-full md:w-1/3">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FontAwesomeIcon
                        icon={faSearch}
                        className="text-gray-400"
                      />
                    </div>
                    <input
                      type="text"
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      placeholder="Search references, customers, status..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {searchTerm && (
                      <button
                        onClick={() => setSearchTerm("")}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </button>
                    )}
                  </div>
                </div>

                {/* Buttons group */}
                <div className="flex flex-wrap gap-2 justify-end ml-auto">
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                      showFilters
                        ? "bg-blue-100 text-blue-700 border border-blue-200"
                        : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <FontAwesomeIcon icon={faFilter} />
                    <span>Filters</span>
                    <FontAwesomeIcon
                      icon={showFilters ? faChevronUp : faChevronDown}
                      className="text-xs"
                    />
                  </button>

                  <button
                    onClick={() => setShowDatePicker(!showDatePicker)}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                      showDatePicker ||
                      (dateRange[0].startDate && dateRange[0].endDate)
                        ? "bg-blue-100 text-blue-700 border border-blue-200"
                        : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <FontAwesomeIcon icon={faCalendarAlt} />
                    <span>Date Range</span>
                    {showDatePicker ? (
                      <FontAwesomeIcon icon={faChevronUp} className="text-xs" />
                    ) : (
                      <FontAwesomeIcon
                        icon={faChevronDown}
                        className="text-xs"
                      />
                    )}
                  </button>

                  <button
                    onClick={clearAllFilters}
                    disabled={!hasActiveFilters}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                      hasActiveFilters
                        ? "bg-red-50 text-red-700 border border-red-200 hover:bg-red-100"
                        : "bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed"
                    }`}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                    <span>Clear All Filters</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Date picker dropdown */}
            {showDatePicker && (
              <div className="border-t border-gray-200 bg-gray-50 p-4">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="font-medium text-gray-700">
                    Select Date Range
                  </h3>
                  <div className="flex gap-2">
                    <button
                      onClick={() =>
                        setDateRange([
                          {
                            startDate: null,
                            endDate: null,
                            key: "selection",
                          },
                        ])
                      }
                      className="text-sm text-gray-500 hover:text-red-600 transition-colors"
                    >
                      Clear Range
                    </button>
                    <button
                      onClick={() => setShowDatePicker(false)}
                      className="text-sm text-gray-500 hover:text-gray-800 transition-colors"
                    >
                      Close
                    </button>
                  </div>
                </div>

                <div className="flex justify-center">
                  <div className="border border-gray-300 rounded-lg shadow-md bg-white">
                    <DateRangePicker
                      ranges={dateRange}
                      onChange={(ranges) => setDateRange([ranges.selection])}
                      months={2}
                      direction="horizontal"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Filter Panel */}
            {showFilters && (
              <div className="border-t border-gray-200 p-4 bg-gray-50">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                  <div className="filter-group">
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                      <FontAwesomeIcon
                        icon={faBuilding}
                        className="mr-2 text-blue-600"
                      />
                      Customer
                    </label>
                    <Select
                      options={filterOptions.customers}
                      isMulti
                      onChange={setSelectedCustomers}
                      value={selectedCustomers}
                      placeholder="Select Customers"
                      classNamePrefix="select-filter"
                      className="text-sm"
                      styles={customSelectStyles}
                    />
                  </div>

                  <div className="filter-group">
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                      <FontAwesomeIcon
                        icon={faClipboardCheck}
                        className="mr-2 text-blue-600"
                      />
                      Status
                    </label>
                    <Select
                      options={filterOptions.status}
                      isMulti
                      onChange={setSelectedStatus}
                      value={selectedStatus}
                      placeholder="Select Status"
                      classNamePrefix="select-filter"
                      className="text-sm"
                      styles={customSelectStyles}
                    />
                  </div>

                  <div className="filter-group">
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                      <FontAwesomeIcon
                        icon={faMapMarkerAlt}
                        className="mr-2 text-blue-600"
                      />
                      Entry Point
                    </label>
                    <Select
                      options={filterOptions.entryPoints}
                      isMulti
                      onChange={setSelectedEntryPoints}
                      value={selectedEntryPoints}
                      placeholder="Select Entry Points"
                      classNamePrefix="select-filter"
                      className="text-sm"
                      styles={customSelectStyles}
                    />
                  </div>

                  <div className="filter-group">
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                      <FontAwesomeIcon
                        icon={faIdCard}
                        className="mr-2 text-blue-600"
                      />
                      Operation Type
                    </label>
                    <Select
                      options={filterOptions.operationTypes}
                      isMulti
                      onChange={setSelectedOperationTypes}
                      value={selectedOperationTypes}
                      placeholder="Select Operation Types"
                      classNamePrefix="select-filter"
                      className="text-sm"
                      styles={customSelectStyles}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Active filters display */}
        {hasActiveFilters && (
          <div className="mb-6 flex flex-wrap gap-2">
            {selectedCustomers.map((item) => (
              <span
                key={item.value}
                className="bg-blue-50 text-blue-700 px-2 py-1 rounded-full flex items-center text-xs transition-all hover:bg-blue-100 border border-blue-200"
              >
                <span className="font-medium mr-1">Customer:</span> {item.label}
                <button
                  onClick={() => {
                    setSelectedCustomers((prev) =>
                      prev.filter((i) => i.value !== item.value)
                    );
                  }}
                  className="ml-1 text-blue-500 hover:text-blue-700 transition-colors"
                >
                  <FontAwesomeIcon icon={faTimes} className="text-xs" />
                </button>
              </span>
            ))}

            {selectedStatus.map((item) => (
              <span
                key={item.value}
                className="bg-blue-50 text-blue-700 px-2 py-1 rounded-full flex items-center text-xs transition-all hover:bg-blue-100 border border-blue-200"
              >
                <span className="font-medium mr-1">Status:</span> {item.label}
                <button
                  onClick={() => {
                    setSelectedStatus((prev) =>
                      prev.filter((i) => i.value !== item.value)
                    );
                  }}
                  className="ml-1 text-blue-500 hover:text-blue-700 transition-colors"
                >
                  <FontAwesomeIcon icon={faTimes} className="text-xs" />
                </button>
              </span>
            ))}

            {selectedEntryPoints.map((item) => (
              <span
                key={item.value}
                className="bg-blue-50 text-blue-700 px-2 py-1 rounded-full flex items-center text-xs transition-all hover:bg-blue-100 border border-blue-200"
              >
                <span className="font-medium mr-1">Entry Point:</span>{" "}
                {item.label}
                <button
                  onClick={() => {
                    setSelectedEntryPoints((prev) =>
                      prev.filter((i) => i.value !== item.value)
                    );
                  }}
                  className="ml-1 text-blue-500 hover:text-blue-700 transition-colors"
                >
                  <FontAwesomeIcon icon={faTimes} className="text-xs" />
                </button>
              </span>
            ))}

            {selectedOperationTypes.map((item) => (
              <span
                key={item.value}
                className="bg-blue-50 text-blue-700 px-2 py-1 rounded-full flex items-center text-xs transition-all hover:bg-blue-100 border border-blue-200"
              >
                <span className="font-medium mr-1">Op. Type:</span> {item.label}
                <button
                  onClick={() => {
                    setSelectedOperationTypes((prev) =>
                      prev.filter((i) => i.value !== item.value)
                    );
                  }}
                  className="ml-1 text-blue-500 hover:text-blue-700 transition-colors"
                >
                  <FontAwesomeIcon icon={faTimes} className="text-xs" />
                </button>
              </span>
            ))}

            {searchTerm && (
              <span className="bg-blue-50 text-blue-700 px-2 py-1 rounded-full flex items-center text-xs transition-all hover:bg-blue-100 border border-blue-200">
                <span className="font-medium mr-1">Search:</span> {searchTerm}
                <button
                  onClick={() => setSearchTerm("")}
                  className="ml-1 text-blue-500 hover:text-blue-700 transition-colors"
                >
                  <FontAwesomeIcon icon={faTimes} className="text-xs" />
                </button>
              </span>
            )}

            {dateRange[0].startDate && dateRange[0].endDate && (
              <span className="bg-blue-50 text-blue-700 px-2 py-1 rounded-full flex items-center text-xs transition-all hover:bg-blue-100 border border-blue-200">
                <FontAwesomeIcon icon={faCalendarAlt} className="mr-1" />
                <span className="font-medium mr-1">Date Range:</span>
                {formatDate(dateRange[0].startDate)} -{" "}
                {formatDate(dateRange[0].endDate)}
                <button
                  onClick={() =>
                    setDateRange([
                      {
                        startDate: null,
                        endDate: null,
                        key: "selection",
                      },
                    ])
                  }
                  className="ml-1 text-blue-500 hover:text-blue-700 transition-colors"
                >
                  <FontAwesomeIcon icon={faTimes} className="text-xs" />
                </button>
              </span>
            )}
          </div>
        )}

        {/* Overview Dashboard or Data Grid based on active tab */}
        {activeTab === "overview" ? (
          <>
            {/* Summary Cards */}
            <DashboardSummaryCards
              metrics={summaryMetrics}
              data={filteredData}
              onShowOutOfTimeModal={showOutOfTimeModal}
              onShowValidationProcessModal={showValidationProcessModal}
              onShowEtaComplianceModal={showEtaComplianceModal}
              onShowProcessingTimeModal={showProcessingTimeModal}
            />

            {/* Charts Layout */}
            <div className={`grid ${chartSizeClasses.columns} gap-6 mt-6`}>
              {/* Status Distribution Chart */}
              <div
                className={`bg-white shadow rounded-lg p-4 border border-gray-200 flex flex-col ${chartSizeClasses.height}`}
              >
                <StatusDistributionChart
                  data={filteredData}
                  onItemClick={(status) => {
                    setSelectedStatus([{ value: status, label: status }]);
                    setActiveTab("data");
                  }}
                />
              </div>

              {/* ETA Compliance Chart */}
              <div
                className={`bg-white shadow rounded-lg p-4 border border-gray-200 flex flex-col ${chartSizeClasses.height}`}
              >
                <EtaComplianceChart
                  data={filteredData}
                  calculateEtaCompliance={calculateEtaCompliance}
                  onItemClick={() => showEtaComplianceModal(false)}
                />
              </div>

              {/* Processing Time Chart */}
              <div
                className={`bg-white shadow rounded-lg p-4 border border-gray-200 flex flex-col ${chartSizeClasses.height}`}
              >
                <ProcessingTimeChart
                  data={filteredData}
                  calculateExitTime={calculateExitTime}
                  calculatePaymentTime={calculatePaymentTime}
                />
              </div>

              {/* Validation Process Table */}
              <div
                className={`bg-white shadow rounded-lg p-4 border border-gray-200 flex flex-col ${chartSizeClasses.height} overflow-auto`}
              >
                <ValidationProcessTable
                  data={filteredData.filter(
                    (item) =>
                      isInValidationProcess(item) || isOutOfTimeValidation(item)
                  )}
                  isInValidationProcess={isInValidationProcess}
                  isOutOfTimeValidation={isOutOfTimeValidation}
                  formatDate={formatDate}
                  onItemClick={handleDetailsClick}
                />
              </div>
            </div>
          </>
        ) : (
          // Data Grid Tab
          <ReferencesDataGrid
            data={paginatedData}
            totalItems={filteredData.length}
            pagination={pagination}
            sortConfig={sortConfig}
            onSort={requestSort}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            onDetailsClick={handleDetailsClick}
            formatDate={formatDate}
            getStatusColorClass={getStatusColorClass}
            getTransportIcon={getTransportIcon}
            getOperationTypeText={getOperationTypeText}
            calculateEtaCompliance={calculateEtaCompliance}
            calculateExitTime={calculateExitTime}
            calculatePaymentTime={calculatePaymentTime}
            isInValidationProcess={isInValidationProcess}
            isOutOfTimeValidation={isOutOfTimeValidation}
          />
        )}

        {/* References Detail Modal */}
        <ReferencesDetailModal
          show={modalConfig.show}
          onClose={closeModal}
          title={modalConfig.title}
          description={modalConfig.description}
          references={modalConfig.references}
          kpiType={modalConfig.kpiType}
          formatDate={formatDate}
          getStatusColorClass={getStatusColorClass}
          getTransportIcon={getTransportIcon}
          onDetailsClick={handleDetailsClick}
        />

        {/* Details Panel (Slide In from right) */}
        {detailsItem && (
          <DetailedReferencePanel
            item={detailsItem}
            show={showDetailsPanel}
            onClose={() => setShowDetailsPanel(false)}
            formatDate={formatDate}
            getStatusColorClass={getStatusColorClass}
            getTransportIcon={getTransportIcon}
            calculateEtaCompliance={calculateEtaCompliance}
            calculateExitTime={calculateExitTime}
            calculatePaymentTime={calculatePaymentTime}
            isInValidationProcess={isInValidationProcess}
            isOutOfTimeValidation={isOutOfTimeValidation}
          />
        )}

        {/* Overlay when details panel is open */}
        {showDetailsPanel && (
          <div
            className="fixed inset-0 bg-black bg-opacity-30 z-30"
            onClick={() => setShowDetailsPanel(false)}
          ></div>
        )}
      </div>
    </div>
  );
};

export default TrafficManagementDashboard;
